import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useToast,
  Text,
  HStack,
  Heading,
  Badge,
  Image,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Textarea,
  Select,
  Flex,
  Tooltip,
  Code,
  useClipboard,
  Link,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Progress,
  Card,
  CardHeader,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  Grid,
  GridItem,
  Container,
  Divider,
  Stack,
  Avatar,
  Tag,
  TagLabel,
  TagLeftIcon,
  Spacer,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  StatNumber,
  StatHelpText,
  List,
  ListItem,
  ListIcon,
  Icon,
} from '@chakra-ui/react';
import { 
  EditIcon, 
  DeleteIcon, 
  ViewIcon, 
  CopyIcon, 
  CheckIcon, 
  LockIcon, 
  ExternalLinkIcon, 
  DownloadIcon, 
  InfoIcon,
  SearchIcon, 
  ChevronDownIcon
} from '@chakra-ui/icons';
import { 
  FaDollarSign, 
  FaUpload, 
  FaFileExcel, 
  FaFileCsv, 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaBuilding,
  FaEdit, 
  FaTrash, 
  FaEye, 
  FaCreditCard,
  FaCalendarAlt,
  FaFilter,
  FaSearch,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt
} from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import { useOutletContext, useNavigate } from 'react-router-dom';

import { getAuthHeaders } from '../../utils/apiAuth';
const LabManagement = () => {
  const [labs, setLabs] = useState([]);
  const [testTypes, setTestTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLab, setSelectedLab] = useState(null);
  const [selectedTestType, setSelectedTestType] = useState(null);
  
  // Grid view and filtering states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, unclaimed, inactive, past_due
  const [planFilter, setPlanFilter] = useState('all'); // all, basic, standard, premium
  const [sortBy, setSortBy] = useState('name'); // name, enrollment_date, next_billing_date, monthly_fee
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100); // Increased to show more labs per page
  
  const { isOpen: isLabModalOpen, onOpen: onLabModalOpen, onClose: onLabModalClose } = useDisclosure();
  const { isOpen: isTestModalOpen, onOpen: onTestModalOpen, onClose: onTestModalClose } = useDisclosure();
  const { isOpen: isTestTypesModalOpen, onOpen: onTestTypesModalOpen, onClose: onTestTypesModalClose } = useDisclosure();
  const { isOpen: isSecretKeyModalOpen, onOpen: onSecretKeyModalOpen, onClose: onSecretKeyModalClose } = useDisclosure();
  const { isOpen: isUploadModalOpen, onOpen: onUploadModalOpen, onClose: onUploadModalClose } = useDisclosure();
  const toast = useToast();
  const { adminUser } = useOutletContext();
  const navigate = useNavigate();
  const [generatingKey, setGeneratingKey] = useState(false);
  const [selectedLabForKey, setSelectedLabForKey] = useState(null);
  const [newSecretKey, setNewSecretKey] = useState('');
  const [labStats, setLabStats] = useState({});
  
  // Upload related state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [duplicateCheckResults, setDuplicateCheckResults] = useState(null);
  const [showDuplicateDetails, setShowDuplicateDetails] = useState(false);

  const [labFormData, setLabFormData] = useState({
    name: '',
    address: '',
    logo_url: '',
    contact_email: '',
    contact_phone: '',
    is_active: true,
    partnership_status: 'unclaimed', // 'active' or 'unclaimed'
    secret_key: '',
    portal_slug: '',
  });

  const [testTypeFormData, setTestTypeFormData] = useState({
    name: '',
    description: '',
    cash_price: '',
    tube_top_color: '',
    is_active: true,
  });

  const tubeTopColors = [
    'Red', 'Purple', 'Blue', 'Green', 'Yellow', 'Gold', 'Orange', 'Pink', 'Gray', 'White'
  ];

  useEffect(() => {
    fetchLabs();
    fetchTestTypes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLabs = async () => {
    try {
      // First, get the total count
      const { count: totalCount, error: countError } = await supabase
        .from('labs')
        .select('*', { count: 'exact', head: true });
      
      if (countError) throw countError;
      
      console.log(`Total labs in database: ${totalCount}`);
      
      // Limit to 5000 labs to prevent crashes
      const maxLabs = Math.min(totalCount, 5000);
      
      let allLabs = [];
      let from = 0;
      const limit = 1000;
      let hasMore = true;

      // Fetch labs in batches, but limit total to prevent crashes
      while (hasMore && allLabs.length < maxLabs) {
        const remaining = maxLabs - allLabs.length;
        const currentLimit = Math.min(limit, remaining);
        
        const { data, error } = await supabase
          .from('labs')
          .select('*')
          .order('created_at', { ascending: false })
          .range(from, from + currentLimit - 1);

        if (error) throw error;
        
        if (data && data.length > 0) {
          allLabs = [...allLabs, ...data];
          from += currentLimit;
          hasMore = data.length === currentLimit && allLabs.length < maxLabs;
        } else {
          hasMore = false;
        }
      }

      setLabs(allLabs);
      console.log(`Fetched ${allLabs.length} labs from database (limited to ${maxLabs})`);
      
      if (totalCount > maxLabs) {
        toast({
          title: 'Large Dataset Warning',
          description: `Showing first ${maxLabs} of ${totalCount} labs. Use search/filters to find specific labs.`,
          status: 'warning',
          duration: 8000,
          isClosable: true,
        });
      }

      // Auto-generate portal slugs for labs that don't have them
      if (allLabs && allLabs.length > 0) {
        const labsNeedingSlugs = allLabs.filter(lab => !lab.portal_slug);
        console.log(`Found ${labsNeedingSlugs.length} labs needing portal slugs:`, labsNeedingSlugs.map(l => l.name));
        
        for (const lab of labsNeedingSlugs) {
          try {
            let baseSlug = generateSlugFromName(lab.name);
            let finalSlug = baseSlug;
            let counter = 0;
            
            console.log(`Generating slug for "${lab.name}": base="${baseSlug}"`);
            
            // Check for conflicts and add number if needed
            // eslint-disable-next-line no-loop-func
            while (allLabs.some(existingLab => existingLab.portal_slug === finalSlug)) {
              counter++;
              finalSlug = `${baseSlug}-${counter}`;
              console.log(`Conflict found, trying: ${finalSlug}`);
            }
            
            console.log(`Final slug for "${lab.name}": ${finalSlug}`);
            
            // Update the lab with the new slug
            const { data: updateData, error: updateError } = await supabase
              .from('labs')
              .update({ portal_slug: finalSlug })
              .eq('id', lab.id)
              .select();
            
            if (!updateError && updateData && updateData.length > 0) {
              console.log(`✅ Successfully updated "${lab.name}" with slug: ${finalSlug}`);
              // Update the local data
              lab.portal_slug = finalSlug;
            } else {
              console.error(`❌ Error updating portal slug for ${lab.name}:`, updateError || 'No data returned');
            }
          } catch (slugError) {
            console.error(`❌ Exception generating slug for ${lab.name}:`, slugError);
          }
        }
        
        // Force refresh if we updated any slugs
        if (labsNeedingSlugs.length > 0) {
          console.log('Refreshing data after slug generation...');
          setTimeout(() => {
            fetchLabs();
          }, 1000);
        }
      }

      // Fetch login statistics for each lab
      if (allLabs && allLabs.length > 0) {
        const stats = {};
        
        for (const lab of allLabs) {
          try {
            // Get total login count
            const { count: totalLogins } = await supabase
              .from('lab_login_log')
              .select('*', { count: 'exact', head: true })
              .eq('lab_id', lab.id)
              .eq('success', true);

            // Get login count in last 30 days
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            
            const { count: recentLogins } = await supabase
              .from('lab_login_log')
              .select('*', { count: 'exact', head: true })
              .eq('lab_id', lab.id)
              .eq('success', true)
              .gte('login_timestamp', thirtyDaysAgo.toISOString());

            stats[lab.id] = {
              totalLogins: totalLogins || 0,
              recentLogins: recentLogins || 0,
              lastLogin: lab.last_login_at,
              hasPortalAccess: !!lab.secret_key
            };
          } catch (statError) {
            console.warn(`Failed to fetch stats for lab ${lab.id}:`, statError);
            stats[lab.id] = {
              totalLogins: 0,
              recentLogins: 0,
              lastLogin: lab.last_login_at,
              hasPortalAccess: !!lab.secret_key
            };
          }
        }
        
        setLabStats(stats);
      }
    } catch (error) {
      toast({
        title: 'Error fetching labs',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTestTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('test_types')
        .select(`
          *,
          labs (name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTestTypes(data || []);
    } catch (error) {
      toast({
        title: 'Error fetching test types',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const generateSlugFromName = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleLabInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setLabFormData((prev) => {
      const updated = {
        ...prev,
        [name]: newValue,
      };
      
      // Auto-generate portal slug when name changes (only if portal_slug is empty)
      if (name === 'name' && value && !prev.portal_slug) {
        updated.portal_slug = generateSlugFromName(value);
      }
      
      return updated;
    });
  };

  const handleTestTypeInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTestTypeFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleTestTypeNumberChange = (name, value) => {
    setTestTypeFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLabSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let actionType;
      if (selectedLab) {
        await supabase
          .from('labs')
          .update(labFormData)
          .eq('id', selectedLab.id);
        actionType = 'Updated Lab';
      } else {
        await supabase
          .from('labs')
          .insert([labFormData]);
        actionType = 'Added Lab';
      }

      if (adminUser && adminUser.name) {
        await supabase.from('admin_activity_log').insert([
          {
            action: actionType,
            username: adminUser.name,
            details: labFormData.name,
            created_at: new Date().toISOString(),
          },
        ]);
      }

      toast({
        title: 'Success',
        description: 'Lab saved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onLabModalClose();
      fetchLabs();
    } catch (error) {
      toast({
        title: 'Error saving lab',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestTypeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const testTypeData = {
        ...testTypeFormData,
        lab_id: selectedLab.id,
        cash_price: parseFloat(testTypeFormData.cash_price),
      };

      let actionType;
      if (selectedTestType) {
        await supabase
          .from('test_types')
          .update(testTypeData)
          .eq('id', selectedTestType.id);
        actionType = 'Updated Test Type';
      } else {
        await supabase
          .from('test_types')
          .insert([testTypeData]);
        actionType = 'Added Test Type';
      }

      if (adminUser && adminUser.name) {
        await supabase.from('admin_activity_log').insert([
          {
            action: actionType,
            username: adminUser.name,
            details: `${testTypeData.name} for ${selectedLab.name}`,
            created_at: new Date().toISOString(),
          },
        ]);
      }

      toast({
        title: 'Success',
        description: 'Test type saved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onTestModalClose();
      fetchTestTypes();
    } catch (error) {
      toast({
        title: 'Error saving test type',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditLab = (lab) => {
    setSelectedLab(lab);
    setLabFormData({
      name: lab.name,
      address: lab.address || '',
      logo_url: lab.logo_url || '',
      contact_email: lab.contact_email || '',
      contact_phone: lab.contact_phone || '',
      is_active: lab.is_active,
      partnership_status: lab.partnership_status || 'unclaimed',
      secret_key: lab.secret_key || '',
      portal_slug: lab.portal_slug || '',
    });
    onLabModalOpen();
  };

  const handleEditTestType = (testType) => {
    setSelectedTestType(testType);
    setTestTypeFormData({
      name: testType.name,
      description: testType.description || '',
      cash_price: testType.cash_price.toString(),
      tube_top_color: testType.tube_top_color || '',
      is_active: testType.is_active,
    });
    onTestModalOpen();
  };

  const handleViewTestTypes = (lab) => {
    setSelectedLab(lab);
    onTestTypesModalOpen();
  };

  const handleDeleteLab = async (id) => {
    if (window.confirm('Are you sure you want to delete this lab? This will also delete all associated test types.')) {
      try {
        const { error } = await supabase
          .from('labs')
          .delete()
          .eq('id', id);

        if (error) throw error;

        toast({
          title: 'Lab deleted successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        fetchLabs();
        fetchTestTypes();
      } catch (error) {
        toast({
          title: 'Error deleting lab',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const handleDeleteTestType = async (id) => {
    if (window.confirm('Are you sure you want to delete this test type?')) {
      try {
        const { error } = await supabase
          .from('test_types')
          .delete()
          .eq('id', id);

        if (error) throw error;

        toast({
          title: 'Test type deleted successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        fetchTestTypes();
      } catch (error) {
        toast({
          title: 'Error deleting test type',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const resetLabForm = () => {
    setLabFormData({
      name: '',
      address: '',
      logo_url: '',
      contact_email: '',
      contact_phone: '',
      is_active: true,
      partnership_status: 'unclaimed',
      secret_key: '',
      portal_slug: '',
    });
    setSelectedLab(null);
  };

  const resetTestTypeForm = () => {
    setSelectedTestType(null);
    setTestTypeFormData({
      name: '',
      description: '',
      cash_price: '',
      tube_top_color: '',
      is_active: true,
    });
  };

  // Upload Functions
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!validTypes.includes(fileExtension)) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a CSV or Excel file (.csv, .xlsx, .xls)',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setUploadFile(file);
    
    // Read and preview file content
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      if (fileExtension === '.csv') {
        parseCSVContent(content);
      } else {
        // For Excel files, we'll need a library like xlsx
        toast({
          title: 'Excel support coming soon',
          description: 'Please use CSV format for now',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    };
    
    if (fileExtension === '.csv') {
      reader.readAsText(file);
    }
  };

  // Proper CSV parsing function that handles quoted fields with commas
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim()); // Add the last field
    return result;
  };

  const parseCSVContent = (content) => {
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      toast({
        title: 'Invalid CSV format',
        description: 'CSV must have a header row and at least one data row',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const headers = parseCSVLine(lines[0]).map(h => h.replace(/"/g, ''));
    const dataRows = lines.slice(1);
    
    // Expected headers with new format
    const expectedHeaders = ['CITY_NAME', 'FAC_NAME', 'ST_ADR', 'STATE_CD', 'ZIP_CD'];
    const missingHeaders = expectedHeaders.filter(h => !headers.includes(h));
    
    if (missingHeaders.length > 0) {
      toast({
        title: 'Missing required columns',
        description: `Required columns: ${missingHeaders.join(', ')}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const parsedData = dataRows.map((row, index) => {
      const values = parseCSVLine(row).map(v => v.replace(/"/g, ''));
      const csvData = {};
      
      // Map CSV headers to values
      headers.forEach((header, i) => {
        csvData[header] = values[i] || '';
      });
      
      // Map CSV fields to database fields
      const labData = {
        name: csvData.FAC_NAME || '',
        address: `${csvData.ST_ADR || ''}, ${csvData.CITY_NAME || ''}, ${csvData.STATE_CD || ''} ${csvData.ZIP_CD || ''}`.trim(),
        contact_phone: csvData.PHNE_NUM || '',
        fax_phone: csvData.FAX_PHNE_NUM || '',
        provider_number: csvData.PRVDR_NUM || '',
        original_participation_date: csvData.ORGNL_PRTCPTN_DT || '',
        city: csvData.CITY_NAME || '',
        state: csvData.STATE_CD || '',
        zip_code: csvData.ZIP_CD || '',
        street_address: csvData.ST_ADR || '',
        partnership_status: 'unclaimed',
        is_active: true,
        row_number: index + 2 // +2 because of header and 0-based index
      };
      
      return labData;
    });

    setPreviewData(parsedData);
    
    // Check for duplicates immediately after parsing
    checkForDuplicates(parsedData);
  };

  // Enhanced duplicate checking function
  const checkForDuplicates = async (dataToCheck) => {
    try {
      // Fetch all existing labs to check for duplicates
      const { data: existingLabs, error: fetchError } = await supabase
        .from('labs')
        .select('name, contact_phone, provider_number, address, street_address, city, state, zip_code');
      
      if (fetchError) {
        console.error('Error fetching existing labs:', fetchError);
        return;
      }

      const duplicates = [];
      const newLabs = [];

      for (let i = 0; i < dataToCheck.length; i++) {
        const labData = dataToCheck[i];
        let reason = '';

        // Check for duplicates using multiple criteria
        const duplicateLab = existingLabs.find(existingLab => {
          // Check by name (case-insensitive)
          if (existingLab.name && labData.name && 
              existingLab.name.toLowerCase().trim() === labData.name.toLowerCase().trim()) {
            reason = 'Duplicate name';
            return true;
          }
          
          // Check by provider number (if both have it)
          if (existingLab.provider_number && labData.provider_number && 
              existingLab.provider_number.trim() === labData.provider_number.trim()) {
            reason = 'Duplicate provider number';
            return true;
          }
          
          // Check by phone number (if both have it)
          if (existingLab.contact_phone && labData.contact_phone) {
            // Clean phone numbers for comparison (remove non-digits)
            const existingPhone = existingLab.contact_phone.replace(/\D/g, '');
            const newPhone = labData.contact_phone.replace(/\D/g, '');
            if (existingPhone && newPhone && existingPhone === newPhone) {
              reason = 'Duplicate phone number';
              return true;
            }
          }
          
          // Check by address combination (street + city + state + zip)
          if (existingLab.street_address && existingLab.city && existingLab.state &&
              labData.street_address && labData.city && labData.state) {
            const existingAddress = `${existingLab.street_address} ${existingLab.city} ${existingLab.state} ${existingLab.zip_code || ''}`.toLowerCase().trim();
            const newAddress = `${labData.street_address} ${labData.city} ${labData.state} ${labData.zip_code || ''}`.toLowerCase().trim();
            if (existingAddress === newAddress) {
              reason = 'Duplicate address';
              return true;
            }
          }
          
          return false;
        });

        if (duplicateLab) {
          duplicates.push({
            ...labData,
            duplicateReason: reason,
            existingLab: duplicateLab,
            rowNumber: i + 2 // +2 because of header and 0-based index
          });
        } else {
          newLabs.push(labData);
        }
      }

      setDuplicateCheckResults({
        total: dataToCheck.length,
        duplicates: duplicates,
        newLabs: newLabs,
        duplicateCount: duplicates.length,
        newCount: newLabs.length
      });

    } catch (error) {
      console.error('Error checking for duplicates:', error);
    }
  };

  const processUpload = async () => {
    if (!previewData.length || !duplicateCheckResults) {
      toast({
        title: 'No data to upload',
        description: 'Please select and preview a file first',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    
    const results = {
      success: 0,
      failed: 0,
      skipped: duplicateCheckResults.duplicateCount,
      errors: [],
      duplicates: duplicateCheckResults.duplicates
    };

    try {
      // Process only the new labs (non-duplicates)
      const labsToProcess = duplicateCheckResults.newLabs;
      
      if (labsToProcess.length === 0) {
        toast({
          title: 'No new labs to upload',
          description: 'All labs in the file are duplicates',
          status: 'info',
          duration: 5000,
          isClosable: true,
        });
        setUploadResults(results);
        setUploading(false);
        return;
      }

      // Process labs in batches to prevent timeouts
      const batchSize = 100;
      const totalBatches = Math.ceil(labsToProcess.length / batchSize);

      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const startIndex = batchIndex * batchSize;
        const endIndex = Math.min(startIndex + batchSize, labsToProcess.length);
        const batch = labsToProcess.slice(startIndex, endIndex);

        // Process batch
        for (let i = 0; i < batch.length; i++) {
          const labData = batch[i];
          
          try {
            // Generate slug for the lab
            const slug = generateSlugFromName(labData.name);
            let finalSlug = slug;
            let counter = 0;
            
            // Check for slug conflicts with existing labs and current batch
            while (labs.some(lab => lab.portal_slug === finalSlug) || 
                   previewData.slice(0, startIndex + i).some(item => item.portal_slug === finalSlug)) {
              counter++;
              finalSlug = `${slug}-${counter}`;
            }
            
            labData.portal_slug = finalSlug;
            
            // Insert the lab with all new fields
            const { error } = await supabase
              .from('labs')
              .insert([{
                name: labData.name,
                address: labData.address || null,
                contact_email: labData.contact_email || null,
                contact_phone: labData.contact_phone || null,
                fax_phone: labData.fax_phone || null,
                logo_url: labData.logo_url || null,
                partnership_status: 'unclaimed',
                is_active: true,
                portal_slug: finalSlug,
                // New CSV fields
                city: labData.city || null,
                state: labData.state || null,
                zip_code: labData.zip_code || null,
                street_address: labData.street_address || null,
                provider_number: labData.provider_number || null,
                original_participation_date: labData.original_participation_date || null
              }]);

            if (error) throw error;
            
            results.success++;
            
            // Log admin activity
            if (adminUser && adminUser.name) {
              await supabase.from('admin_activity_log').insert([
                {
                  action: 'Uploaded Lab',
                  username: adminUser.name,
                  details: `Bulk upload: ${labData.name}`,
                  created_at: new Date().toISOString(),
                },
              ]);
            }
            
          } catch (error) {
            console.error(`Error uploading lab ${labData.name}:`, error);
            results.failed++;
            results.errors.push({
              row: labData.row_number,
              name: labData.name,
              error: error.message
            });
          }
        }
        
        // Update progress
        setUploadProgress(((batchIndex + 1) / totalBatches) * 100);
      }

      setUploadResults(results);
      
      // Refresh labs list
      await fetchLabs();
      
      toast({
        title: 'Upload completed',
        description: `Successfully uploaded ${results.success} labs. ${results.skipped} duplicates skipped. ${results.failed} failed.`,
        status: results.failed === 0 ? 'success' : 'warning',
        duration: 5000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Upload process failed:', error);
      toast({
        title: 'Upload failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploading(false);
    }
  };

  const downloadSampleCSV = () => {
    const headers = ['CITY_NAME', 'FAC_NAME', 'ORGNL_PRTCPTN_DT', 'PRVDR_NUM', 'STATE_CD', 'ST_ADR', 'PHNE_NUM', 'ZIP_CD', 'FAX_PHNE_NUM'];
    const sampleData = [
      ['New York', 'Sample Laboratory Inc', '2020-01-15', 'LAB001', 'NY', '123 Medical Drive', '5551234567', '10001', '5551234568'],
      ['Los Angeles', 'Quality Diagnostics Corp', '2019-06-20', 'LAB002', 'CA', '456 Healthcare Blvd', '5559876543', '90210', '5559876544']
    ];
    
    const csvContent = [
      headers.join(','),
      ...sampleData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lab_upload_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const resetUploadModal = () => {
    setUploadFile(null);
    setPreviewData([]);
    setUploadResults(null);
    setUploadProgress(0);
    setUploading(false);
    setDuplicateCheckResults(null);
    setShowDuplicateDetails(false);
  };

  const labTestTypes = testTypes.filter(test => test.lab_id === selectedLab?.id);

  const setSecretKey = async (labId, secretKey) => {
    const trimmedKey = (secretKey || '').trim();
    if (!trimmedKey) {
      toast({ title: 'Key required', description: 'Please enter a secret key.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setGeneratingKey(true);
    const apiBase = process.env.REACT_APP_API_URL || '';
    try {
      const response = await fetch(`${apiBase}/api/set-lab-secret-key`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ labId, secretKey: trimmedKey }),
      });
      let result;
      try {
        result = await response.json();
      } catch {
        throw new Error('API not available. For local dev run "vercel dev" or set REACT_APP_API_URL to your deployed URL.');
      }
      if (!response.ok) throw new Error(result.error || 'Failed to save key');
      setLabs(prevLabs => prevLabs.map(lab =>
        lab.id === labId ? { ...lab, secret_key: trimmedKey } : lab
      ));
      if (adminUser && adminUser.name) {
        await supabase.from('admin_activity_log').insert([
          {
            action: 'Updated Lab Secret Key',
            username: adminUser.name,
            details: `Lab ID: ${labId}`,
            created_at: new Date().toISOString(),
          },
        ]);
      }
      toast({
        title: 'Secret key updated',
        description: result.message || 'Secret key has been updated for the lab portal',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onSecretKeyModalClose();
      setNewSecretKey('');
    } catch (error) {
      toast({
        title: 'Error updating secret key',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleGenerateSecretKey = (lab) => {
    setSelectedLabForKey(lab);
    setNewSecretKey(lab.secret_key || '');
    onSecretKeyModalOpen();
  };

  const generatePortalSlugs = async () => {
    console.log('🔧 Manually generating portal slugs...');
    setLoading(true);
    
    try {
      // First check if portal_slug column exists by trying to select it
      const { error: testError } = await supabase
        .from('labs')
        .select('portal_slug')
        .limit(1);
      
      if (testError && testError.message.includes('column "portal_slug" does not exist')) {
        toast({
          title: 'Portal Slug Column Missing',
          description: 'The portal_slug column needs to be added to the database. Please run the migration script.',
          status: 'error',
          duration: 10000,
          isClosable: true,
        });
        console.error('❌ portal_slug column does not exist');
        return;
      }
      
      // Auto-generate slugs for all labs that don't have them
      const labsToUpdate = labs.filter(lab => !lab.portal_slug);
      console.log(`Found ${labsToUpdate.length} labs needing portal slugs`);
      
      if (labsToUpdate.length === 0) {
        toast({
          title: 'All Labs Have Portal URLs',
          description: 'All labs already have unique portal URLs',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      for (const lab of labsToUpdate) {
        let baseSlug = generateSlugFromName(lab.name);
        let finalSlug = baseSlug;
        let counter = 0;
        
        // Check for conflicts with existing slugs
        // eslint-disable-next-line no-loop-func
        while (labs.some(existingLab => existingLab.portal_slug === finalSlug)) {
          counter++;
          finalSlug = `${baseSlug}-${counter}`;
        }
        
        console.log(`🔄 Updating "${lab.name}" with slug: ${finalSlug}`);
        
        const { error } = await supabase
          .from('labs')
          .update({ portal_slug: finalSlug })
          .eq('id', lab.id)
          .select();
        
        if (error) {
          console.error(`❌ Error updating ${lab.name}:`, error);
          toast({
            title: `Error updating ${lab.name}`,
            description: error.message,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        } else {
          console.log(`✅ Successfully updated ${lab.name} with slug: ${finalSlug}`);
        }
      }
      
      // Refresh the data
      await fetchLabs();
      
      toast({
        title: 'Portal URLs Generated',
        description: `Generated unique portal URLs for ${labsToUpdate.length} labs`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
    } catch (error) {
      console.error('💥 Generate slugs failed:', error);
      toast({
        title: 'Error generating portal URLs',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const PortalInfoCell = ({ lab }) => {
    // Create unique portal URL using portal_slug if available, otherwise fallback to generic login
    const portalUrl = lab.portal_slug 
      ? `${window.location.origin}/lab-portal/${lab.portal_slug}`
      : `${window.location.origin}/lab-portal/login`;
    
    const relativeUrl = lab.portal_slug 
      ? `/lab-portal/${lab.portal_slug}`
      : `/lab-portal/login`;
    
    const { hasCopied: hasPortalCopied, onCopy: onPortalCopy } = useClipboard(portalUrl);
    const { hasCopied: hasKeyCopied, onCopy: onKeyCopy } = useClipboard(lab.secret_key || '');

    return (
      <VStack align="start" spacing={2} minW="300px">
        <Box>
          <Text fontSize="xs" color="gray.600" mb={1}>
            {lab.portal_slug ? 'Branded Portal URL:' : 'Portal URL:'}
          </Text>
          <VStack align="start" spacing={1}>
            <Link 
              href={relativeUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              color="blue.500"
              fontSize="sm"
              fontWeight="medium"
              _hover={{ textDecoration: 'underline' }}
            >
              {portalUrl}
            </Link>
            <HStack spacing={2}>
              <Button
                size="xs"
                leftIcon={<ExternalLinkIcon />}
                colorScheme="blue"
                variant="outline"
                onClick={() => window.open(relativeUrl, '_blank')}
              >
                Open Portal
              </Button>
              <Tooltip label={hasPortalCopied ? "Copied!" : "Copy portal URL"}>
                <IconButton
                  icon={hasPortalCopied ? <CheckIcon color="green.500" /> : <CopyIcon />}
                  size="xs"
                  onClick={onPortalCopy}
                  aria-label="Copy portal URL"
                />
              </Tooltip>
            </HStack>
          </VStack>
          {lab.portal_slug && (
            <Text fontSize="xs" color="blue.600" fontStyle="italic" mt={1}>
              Personalized for {lab.name}
            </Text>
          )}
        </Box>
        
        {lab.secret_key && (
          <Box>
            <Text fontSize="xs" color="gray.600" mb={1}>Secret Key:</Text>
            <HStack spacing={2}>
              <Code fontSize="xs" maxW="200px" isTruncated>{lab.secret_key}</Code>
              <Tooltip label={hasKeyCopied ? "Copied!" : "Copy secret key"}>
                <IconButton
                  icon={hasKeyCopied ? <CheckIcon color="green.500" /> : <CopyIcon />}
                  size="xs"
                  onClick={onKeyCopy}
                  aria-label="Copy secret key"
                />
              </Tooltip>
            </HStack>
          </Box>
        )}
        
        <Button
          size="xs"
          leftIcon={<LockIcon />}
          colorScheme={lab.secret_key ? "orange" : "blue"}
          onClick={() => handleGenerateSecretKey(lab)}
        >
          {lab.secret_key ? "Update Key" : "Set Key"}
        </Button>
      </VStack>
    );
  };

  const LabStatsCell = ({ lab }) => {
    const stats = labStats[lab.id];
    
    if (!stats) {
      return <Text fontSize="sm" color="gray.400">Loading...</Text>;
    }

    const formatLastLogin = (lastLogin) => {
      if (!lastLogin) return 'Never';
      
      const loginDate = new Date(lastLogin);
      const now = new Date();
      const diffInMs = now - loginDate;
      const diffInHours = diffInMs / (1000 * 60 * 60);
      const diffInDays = diffInHours / 24;

      if (diffInHours < 1) {
        const minutes = Math.floor(diffInMs / (1000 * 60));
        return `${minutes}m ago`;
      } else if (diffInHours < 24) {
        return `${Math.floor(diffInHours)}h ago`;
      } else if (diffInDays < 7) {
        return `${Math.floor(diffInDays)}d ago`;
      } else {
        return loginDate.toLocaleDateString();
      }
    };

    const getLastLoginColor = (lastLogin) => {
      if (!lastLogin) return 'gray.500';
      
      const loginDate = new Date(lastLogin);
      const now = new Date();
      const diffInDays = (now - loginDate) / (1000 * 60 * 60 * 24);

      if (diffInDays <= 1) return 'green.500';
      if (diffInDays <= 7) return 'yellow.500';
      if (diffInDays <= 30) return 'orange.500';
      return 'red.500';
    };

    return (
      <VStack align="start" spacing={1}>
        <Box>
          <Text fontSize="xs" color="gray.600">Last Login:</Text>
          <Text 
            fontSize="sm" 
            fontWeight="medium"
            color={getLastLoginColor(stats.lastLogin)}
          >
            {formatLastLogin(stats.lastLogin)}
          </Text>
        </Box>
        
        <Box>
          <Text fontSize="xs" color="gray.600">Total Logins:</Text>
          <Text fontSize="sm" fontWeight="medium">
            {stats.totalLogins}
          </Text>
        </Box>
        
        <Box>
          <Text fontSize="xs" color="gray.600">Last 30 days:</Text>
          <Text fontSize="sm" fontWeight="medium">
            {stats.recentLogins}
          </Text>
        </Box>

        {!stats.hasPortalAccess && (
          <Badge colorScheme="gray" size="sm">
            No Portal Access
          </Badge>
        )}
      </VStack>
    );
  };

  return (
    <Box p={6}>
      <Tabs>
        <TabList>
          <Tab>Labs</Tab>
          <Tab>All Test Types</Tab>
        </TabList>

        <TabPanels>
          <TabPanel>
            <Flex justify="space-between" align="center" mb={4}>
              <Heading size="lg">Lab Partnerships</Heading>
              <HStack spacing={3}>
                <Button 
                  leftIcon={<Icon as={FaUpload} />}
                  colorScheme="green" 
                  onClick={() => { resetUploadModal(); onUploadModalOpen(); }}
                  size="md"
                >
                  Upload Labs
                </Button>
                <Button colorScheme="blue" onClick={() => { resetLabForm(); onLabModalOpen(); }}>
                  Add New Lab
                </Button>
              </HStack>
            </Flex>

            {/* Lab Statistics Summary */}
            <Box mb={6} p={4} bg="gray.50" borderRadius="md">
              <Heading size="md" mb={3}>Portal Usage Summary</Heading>
              <Flex gap={6} wrap="wrap">
                <Box>
                  <Text fontSize="sm" color="gray.600">Total Labs</Text>
                  <Text fontSize="2xl" fontWeight="bold">{labs.length}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.600">With Portal Access</Text>
                  <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                    {Object.values(labStats).filter(stat => stat.hasPortalAccess).length}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.600">Active in Last 30 Days</Text>
                  <Text fontSize="2xl" fontWeight="bold" color="green.500">
                    {Object.values(labStats).filter(stat => stat.recentLogins > 0).length}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.600">Total Logins</Text>
                  <Text fontSize="2xl" fontWeight="bold" color="purple.500">
                    {Object.values(labStats).reduce((sum, stat) => sum + stat.totalLogins, 0)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.600">Last 30 Days</Text>
                  <Text fontSize="2xl" fontWeight="bold" color="orange.500">
                    {Object.values(labStats).reduce((sum, stat) => sum + stat.recentLogins, 0)}
                  </Text>
                </Box>
              </Flex>
            </Box>

            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Logo</Th>
                  <Th>Name</Th>
                  <Th>Address</Th>
                  <Th>Contact</Th>
                  <Th>Status</Th>
                  <Th>Partnership</Th>
                  <Th>Login Statistics</Th>
                  <Th>Portal Info</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {labs.map((lab) => (
                  <Tr key={lab.id}>
                    <Td>
                      {lab.logo_url && (
                        <Image src={lab.logo_url} alt={`${lab.name} logo`} boxSize="50px" objectFit="contain" />
                      )}
                    </Td>
                    <Td fontWeight="bold">{lab.name}</Td>
                    <Td>{lab.address}</Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Text fontSize="sm">{lab.contact_email}</Text>
                        <Text fontSize="sm">{lab.contact_phone}</Text>
                      </VStack>
                    </Td>
                    <Td>
                      <Badge colorScheme={lab.is_active ? 'green' : 'red'}>
                        {lab.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge 
                        colorScheme={lab.partnership_status === 'active' ? 'blue' : 'orange'}
                        variant={lab.partnership_status === 'active' ? 'solid' : 'subtle'}
                      >
                        {lab.partnership_status === 'active' ? 'Partnered' : 'Unclaimed'}
                      </Badge>
                    </Td>
                    <Td>
                      <LabStatsCell lab={lab} />
                    </Td>
                    <Td>
                      <PortalInfoCell lab={lab} />
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Tooltip label="View Lab Profile Page" hasArrow>
                          <IconButton
                            icon={<Icon as={FaBuilding} />}
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            onClick={() => window.open(`/lab-directory/${lab.id}`, '_blank')}
                            aria-label="View lab profile"
                          />
                        </Tooltip>
                        {lab.short_slug && (
                          <Tooltip label="View Public Test Directory" hasArrow>
                            <IconButton
                              icon={<ExternalLinkIcon />}
                              size="sm"
                              colorScheme="green"
                              onClick={() => window.open(`/lab-tests/${lab.short_slug}`, '_blank')}
                              aria-label="View lab directory"
                            />
                          </Tooltip>
                        )}
                        <IconButton
                          icon={<ViewIcon />}
                          size="sm"
                          onClick={() => handleViewTestTypes(lab)}
                          aria-label="View test types"
                        />
                        <IconButton
                          icon={<EditIcon />}
                          size="sm"
                          onClick={() => handleEditLab(lab)}
                          aria-label="Edit lab"
                        />
                        <IconButton
                          icon={<DeleteIcon />}
                          size="sm"
                          colorScheme="red"
                          onClick={() => handleDeleteLab(lab.id)}
                          aria-label="Delete lab"
                        />
                        <Tooltip label="Manage Test Costs & Pricing" hasArrow>
                          <IconButton
                            icon={<Icon as={FaDollarSign} />}
                            size="sm"
                            colorScheme="purple"
                            onClick={() => navigate(`/admin/lab-test-costs/${lab.id}`)}
                            aria-label="View test costs"
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TabPanel>

          <TabPanel>
            <Flex justify="space-between" align="center" mb={4}>
              <Heading size="lg">All Test Types</Heading>
            </Flex>

            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Test Name</Th>
                  <Th>Lab</Th>
                  <Th>Description</Th>
                  <Th>Cash Price</Th>
                  <Th>Tube Color</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {testTypes.map((testType) => (
                  <Tr key={testType.id}>
                    <Td fontWeight="bold">{testType.name}</Td>
                    <Td>{testType.labs?.name}</Td>
                    <Td>{testType.description}</Td>
                    <Td>${testType.cash_price}</Td>
                    <Td>
                      <Badge colorScheme="blue">{testType.tube_top_color}</Badge>
                    </Td>
                    <Td>
                      <Badge colorScheme={testType.is_active ? 'green' : 'red'}>
                        {testType.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </Td>
                    <Td>
                      <IconButton
                        icon={<EditIcon />}
                        size="sm"
                        onClick={() => handleEditTestType(testType)}
                        aria-label="Edit test type"
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Lab Modal */}
      <Modal isOpen={isLabModalOpen} onClose={onLabModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedLab ? 'Edit Lab' : 'Add New Lab'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <form onSubmit={handleLabSubmit}>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Lab Name</FormLabel>
                  <Input
                    name="name"
                    value={labFormData.name}
                    onChange={handleLabInputChange}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Address</FormLabel>
                  <Input
                    name="address"
                    value={labFormData.address}
                    onChange={handleLabInputChange}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Logo URL</FormLabel>
                  <Input
                    name="logo_url"
                    value={labFormData.logo_url}
                    onChange={handleLabInputChange}
                    placeholder="/path/to/logo.png"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Contact Email</FormLabel>
                  <Input
                    name="contact_email"
                    type="email"
                    value={labFormData.contact_email}
                    onChange={handleLabInputChange}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Contact Phone</FormLabel>
                  <Input
                    name="contact_phone"
                    value={labFormData.contact_phone}
                    onChange={handleLabInputChange}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select
                    name="is_active"
                    value={labFormData.is_active}
                    onChange={handleLabInputChange}
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Partnership Status</FormLabel>
                  <Select
                    name="partnership_status"
                    value={labFormData.partnership_status}
                    onChange={handleLabInputChange}
                  >
                    <option value="unclaimed">Unclaimed</option>
                    <option value="active">Partnered</option>
                  </Select>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Existing labs should be "Partnered", uploaded labs default to "Unclaimed"
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>Secret Key</FormLabel>
                  <Input
                    name="secret_key"
                    value={labFormData.secret_key}
                    onChange={handleLabInputChange}
                    placeholder="Lab portal password"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Portal URL Slug</FormLabel>
                  <Input
                    name="portal_slug"
                    value={labFormData.portal_slug}
                    onChange={handleLabInputChange}
                    placeholder="e.g., quality-lab"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Creates a branded URL like: /lab-portal/quality-lab
                  </Text>
                </FormControl>

                {selectedLab && (
                  <FormControl>
                    <FormLabel>Test Menu File</FormLabel>
                    {selectedLab.test_menu_file ? (
                      <VStack align="start" spacing={2}>
                        <HStack>
                          <Icon as={FaFileCsv} color="blue.500" />
                          <Text fontSize="sm" fontWeight="medium">
                            {selectedLab.test_menu_filename || 'Test Menu File'}
                          </Text>
                          {selectedLab.test_menu_uploaded_at && (
                            <Text fontSize="xs" color="gray.500">
                              (Uploaded: {new Date(selectedLab.test_menu_uploaded_at).toLocaleDateString()})
                            </Text>
                          )}
                        </HStack>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          leftIcon={<DownloadIcon />}
                          onClick={() => window.open(selectedLab.test_menu_file, '_blank')}
                        >
                          Download Test Menu
                        </Button>
                      </VStack>
                    ) : (
                      <Text fontSize="sm" color="gray.500" fontStyle="italic">
                        No test menu file uploaded yet
                      </Text>
                    )}
                  </FormControl>
                )}
              </VStack>
            </form>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onLabModalClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleLabSubmit} isLoading={loading}>
              {selectedLab ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Test Type Modal */}
      <Modal isOpen={isTestModalOpen} onClose={onTestModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedTestType ? 'Edit Test Type' : 'Add New Test Type'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <form onSubmit={handleTestTypeSubmit}>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Test Name</FormLabel>
                  <Input
                    name="name"
                    value={testTypeFormData.name}
                    onChange={handleTestTypeInputChange}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    name="description"
                    value={testTypeFormData.description}
                    onChange={handleTestTypeInputChange}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Cash Price</FormLabel>
                  <NumberInput
                    min={0}
                    precision={2}
                    value={testTypeFormData.cash_price}
                    onChange={(valueString, valueNumber) => handleTestTypeNumberChange('cash_price', valueNumber)}
                  >
                    <NumberInputField
                      placeholder="0.00"
                    />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>Tube Top Color</FormLabel>
                  <Select
                    name="tube_top_color"
                    value={testTypeFormData.tube_top_color}
                    onChange={handleTestTypeInputChange}
                    placeholder="Select tube color"
                  >
                    {tubeTopColors.map((color) => (
                      <option key={color} value={color}>{color}</option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select
                    name="is_active"
                    value={testTypeFormData.is_active}
                    onChange={handleTestTypeInputChange}
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </Select>
                </FormControl>
              </VStack>
            </form>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onTestModalClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleTestTypeSubmit} isLoading={loading}>
              {selectedTestType ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Test Types View Modal */}
      <Modal isOpen={isTestTypesModalOpen} onClose={onTestTypesModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Test Types for {selectedLab?.name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Flex justify="space-between" align="center">
                <Text fontWeight="bold">Test Types ({labTestTypes.length})</Text>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={() => { resetTestTypeForm(); onTestModalOpen(); onTestTypesModalClose(); }}
                >
                  Add Test Type
                </Button>
              </Flex>

              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Test Name</Th>
                    <Th>Description</Th>
                    <Th>Cash Price</Th>
                    <Th>Tube Color</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {labTestTypes.map((testType) => (
                    <Tr key={testType.id}>
                      <Td fontWeight="bold">{testType.name}</Td>
                      <Td>{testType.description}</Td>
                      <Td>${testType.cash_price}</Td>
                      <Td>
                        <Badge colorScheme="blue">{testType.tube_top_color}</Badge>
                      </Td>
                      <Td>
                        <HStack spacing={1}>
                          <IconButton
                            icon={<EditIcon />}
                            size="xs"
                            onClick={() => { handleEditTestType(testType); onTestTypesModalClose(); onTestModalOpen(); }}
                            aria-label="Edit test type"
                          />
                          <IconButton
                            icon={<DeleteIcon />}
                            size="xs"
                            colorScheme="red"
                            onClick={() => handleDeleteTestType(testType.id)}
                            aria-label="Delete test type"
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onTestTypesModalClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Secret Key Modal */}
      <Modal 
        isOpen={isSecretKeyModalOpen} 
        onClose={() => { onSecretKeyModalClose(); setNewSecretKey(''); }} 
        size="md"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Set Secret Key for {selectedLabForKey?.name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                {selectedLabForKey?.secret_key 
                  ? 'Setting a new secret key will replace the current one. This will require the lab to use the new key for portal access.'
                  : 'Set a unique secret key for lab portal access. This will act as the password for the lab portal.'
                }
              </Text>
              {selectedLabForKey?.secret_key && (
                <Box p={3} bg="yellow.50" borderRadius="md" borderLeft="4px solid" borderColor="yellow.400">
                  <Text fontSize="sm" fontWeight="medium" color="yellow.800">
                    Warning: Current secret key will be replaced
                  </Text>
                </Box>
              )}
              <FormControl isRequired>
                <FormLabel>Secret Key</FormLabel>
                <Input
                  type="password"
                  value={newSecretKey}
                  onChange={(e) => setNewSecretKey(e.target.value)}
                  placeholder="Enter a secure secret key"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newSecretKey.trim()) {
                      setSecretKey(selectedLabForKey?.id, newSecretKey);
                    }
                  }}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Choose a strong, unique password that the lab will use to access their portal.
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => { onSecretKeyModalClose(); setNewSecretKey(''); }}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              isLoading={generatingKey}
              isDisabled={!newSecretKey.trim()}
              onClick={() => setSecretKey(selectedLabForKey?.id, newSecretKey)}
            >
              {selectedLabForKey?.secret_key ? 'Update Key' : 'Set Key'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Upload Modal */}
      <Modal isOpen={isUploadModalOpen} onClose={() => { onUploadModalClose(); resetUploadModal(); }} size="4xl">
        <ModalOverlay />
        <ModalContent maxW="90vw" maxH="90vh">
          <ModalHeader>
            <HStack>
              <Icon as={FaUpload} color="green.500" />
              <Text>Upload Laboratory Directory</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody maxH="70vh" overflowY="auto">
            <VStack spacing={6} align="stretch">
              {/* Instructions */}
              <Card>
                <CardHeader>
                  <Heading size="sm">Upload Instructions</Heading>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={3}>
                    <Text fontSize="sm" color="gray.600">
                      Upload a CSV file containing laboratory information. All uploaded labs will be marked as "Unclaimed" status.
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">Required columns:</Text>
                    <List spacing={1} fontSize="sm">
                      <ListItem>
                        <ListIcon as={FaCheckCircle} color="green.500" />
                        <strong>FAC_NAME</strong> - Laboratory facility name (required)
                      </ListItem>
                      <ListItem>
                        <ListIcon as={FaCheckCircle} color="green.500" />
                        <strong>ST_ADR</strong> - Street address (required)
                      </ListItem>
                      <ListItem>
                        <ListIcon as={FaCheckCircle} color="green.500" />
                        <strong>CITY_NAME</strong> - City name (required)
                      </ListItem>
                      <ListItem>
                        <ListIcon as={FaCheckCircle} color="green.500" />
                        <strong>STATE_CD</strong> - State code (required)
                      </ListItem>
                      <ListItem>
                        <ListIcon as={FaCheckCircle} color="green.500" />
                        <strong>ZIP_CD</strong> - ZIP code (required)
                      </ListItem>
                    </List>
                    <Text fontSize="sm" fontWeight="medium">Optional columns:</Text>
                    <List spacing={1} fontSize="sm">
                      <ListItem>
                        <ListIcon as={InfoIcon} color="blue.500" />
                        <strong>PHNE_NUM</strong> - Phone number
                      </ListItem>
                      <ListItem>
                        <ListIcon as={InfoIcon} color="blue.500" />
                        <strong>FAX_PHNE_NUM</strong> - Fax number
                      </ListItem>
                      <ListItem>
                        <ListIcon as={InfoIcon} color="blue.500" />
                        <strong>PRVDR_NUM</strong> - Provider number
                      </ListItem>
                      <ListItem>
                        <ListIcon as={InfoIcon} color="blue.500" />
                        <strong>ORGNL_PRTCPTN_DT</strong> - Original participation date (YYYY-MM-DD)
                      </ListItem>
                    </List>
                    <HStack>
                      <Button 
                        size="sm" 
                        leftIcon={<DownloadIcon />} 
                        onClick={downloadSampleCSV}
                        colorScheme="blue"
                        variant="outline"
                      >
                        Download Sample CSV
                      </Button>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>

              {/* File Upload */}
              <Card>
                <CardHeader>
                  <Heading size="sm">Select File</Heading>
                </CardHeader>
                <CardBody>
                  <FormControl>
                    <Input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileUpload}
                      p={1}
                    />
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Supported formats: CSV (.csv), Excel (.xlsx, .xls)
                    </Text>
                  </FormControl>
                  
                  {uploadFile && (
                    <Alert status="info" mt={3}>
                      <AlertIcon />
                      <VStack align="start" spacing={1}>
                        <Text fontSize="sm" fontWeight="medium">File selected: {uploadFile.name}</Text>
                        <Text fontSize="xs">{previewData.length} rows ready for upload</Text>
                      </VStack>
                    </Alert>
                  )}
                </CardBody>
              </Card>

              {/* Duplicate Check Results */}
              {duplicateCheckResults && (
                <Card>
                  <CardHeader>
                    <HStack justify="space-between">
                      <Heading size="sm">Upload Analysis</Heading>
                      <Badge colorScheme={duplicateCheckResults.duplicateCount > 0 ? "orange" : "green"} variant="subtle">
                        {duplicateCheckResults.duplicateCount} duplicates, {duplicateCheckResults.newCount} new
                      </Badge>
                    </HStack>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      <SimpleGrid columns={2} spacing={4}>
                        <Box p={3} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                          <Text fontSize="sm" fontWeight="bold" color="green.700">New Labs</Text>
                          <Text fontSize="2xl" fontWeight="bold" color="green.600">{duplicateCheckResults.newCount}</Text>
                          <Text fontSize="xs" color="green.600">Will be uploaded</Text>
                        </Box>
                        <Box p={3} bg="orange.50" borderRadius="md" border="1px solid" borderColor="orange.200">
                          <Text fontSize="sm" fontWeight="bold" color="orange.700">Duplicates</Text>
                          <Text fontSize="2xl" fontWeight="bold" color="orange.600">{duplicateCheckResults.duplicateCount}</Text>
                          <Text fontSize="xs" color="orange.600">Will be skipped</Text>
                        </Box>
                      </SimpleGrid>

                      {duplicateCheckResults.duplicateCount > 0 && (
                        <Box>
                          <HStack justify="space-between" mb={2}>
                            <Text fontSize="sm" fontWeight="bold">Duplicate Details</Text>
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={() => setShowDuplicateDetails(!showDuplicateDetails)}
                            >
                              {showDuplicateDetails ? 'Hide' : 'Show'} Details
                            </Button>
                          </HStack>
                          
                          {showDuplicateDetails && (
                            <Box maxH="200px" overflowY="auto" border="1px solid" borderColor="gray.200" borderRadius="md">
                              <Table variant="simple" size="sm">
                                <Thead>
                                  <Tr>
                                    <Th>Row</Th>
                                    <Th>Lab Name</Th>
                                    <Th>Reason</Th>
                                    <Th>Existing Lab</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {duplicateCheckResults.duplicates.slice(0, 20).map((dup, index) => (
                                    <Tr key={index}>
                                      <Td fontSize="xs">{dup.rowNumber}</Td>
                                      <Td fontSize="xs" fontWeight="medium">{dup.name}</Td>
                                      <Td fontSize="xs">
                                        <Badge colorScheme="orange" variant="subtle" size="sm">
                                          {dup.duplicateReason}
                                        </Badge>
                                      </Td>
                                      <Td fontSize="xs" color="gray.600">
                                        {dup.existingLab.name}
                                      </Td>
                                    </Tr>
                                  ))}
                                  {duplicateCheckResults.duplicates.length > 20 && (
                                    <Tr>
                                      <Td colSpan={4} textAlign="center" color="gray.500" fontSize="xs">
                                        ... and {duplicateCheckResults.duplicates.length - 20} more duplicates
                                      </Td>
                                    </Tr>
                                  )}
                                </Tbody>
                              </Table>
                            </Box>
                          )}
                        </Box>
                      )}
                    </VStack>
                  </CardBody>
                </Card>
              )}

              {/* Field Mapping Preview */}
              {previewData.length > 0 && (
                <Card>
                  <CardHeader>
                    <Heading size="sm">Field Mapping Preview</Heading>
                    <Text fontSize="sm" color="gray.600">How your CSV columns will be mapped to database fields</Text>
                  </CardHeader>
                  <CardBody>
                    <Box overflowX="auto">
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>CSV Column</Th>
                            <Th>Database Field</Th>
                            <Th>Sample Value</Th>
                            <Th>Status</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          <Tr>
                            <Td fontWeight="bold">FAC_NAME</Td>
                            <Td>name</Td>
                            <Td fontSize="sm">{previewData[0]?.name || 'N/A'}</Td>
                            <Td><Badge colorScheme="green" variant="subtle" size="sm">Required</Badge></Td>
                          </Tr>
                          <Tr>
                            <Td fontWeight="bold">ST_ADR</Td>
                            <Td>street_address</Td>
                            <Td fontSize="sm">{previewData[0]?.street_address || 'N/A'}</Td>
                            <Td><Badge colorScheme="green" variant="subtle" size="sm">Required</Badge></Td>
                          </Tr>
                          <Tr>
                            <Td fontWeight="bold">CITY_NAME</Td>
                            <Td>city</Td>
                            <Td fontSize="sm">{previewData[0]?.city || 'N/A'}</Td>
                            <Td><Badge colorScheme="green" variant="subtle" size="sm">Required</Badge></Td>
                          </Tr>
                          <Tr>
                            <Td fontWeight="bold">STATE_CD</Td>
                            <Td>state</Td>
                            <Td fontSize="sm">{previewData[0]?.state || 'N/A'}</Td>
                            <Td><Badge colorScheme="green" variant="subtle" size="sm">Required</Badge></Td>
                          </Tr>
                          <Tr>
                            <Td fontWeight="bold">ZIP_CD</Td>
                            <Td>zip_code</Td>
                            <Td fontSize="sm">{previewData[0]?.zip_code || 'N/A'}</Td>
                            <Td><Badge colorScheme="green" variant="subtle" size="sm">Required</Badge></Td>
                          </Tr>
                          <Tr>
                            <Td fontWeight="bold">PHNE_NUM</Td>
                            <Td>contact_phone</Td>
                            <Td fontSize="sm">{previewData[0]?.contact_phone || 'N/A'}</Td>
                            <Td><Badge colorScheme="blue" variant="subtle" size="sm">Optional</Badge></Td>
                          </Tr>
                          <Tr>
                            <Td fontWeight="bold">FAX_PHNE_NUM</Td>
                            <Td>fax_phone</Td>
                            <Td fontSize="sm">{previewData[0]?.fax_phone || 'N/A'}</Td>
                            <Td><Badge colorScheme="blue" variant="subtle" size="sm">Optional</Badge></Td>
                          </Tr>
                          <Tr>
                            <Td fontWeight="bold">PRVDR_NUM</Td>
                            <Td>provider_number</Td>
                            <Td fontSize="sm">{previewData[0]?.provider_number || 'N/A'}</Td>
                            <Td><Badge colorScheme="blue" variant="subtle" size="sm">Optional</Badge></Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </Box>
                  </CardBody>
                </Card>
              )}

              {/* Sample Data Preview */}
              {previewData.length > 0 && (
                <Card>
                  <CardHeader>
                    <Heading size="sm">Sample Data Preview</Heading>
                    <Text fontSize="sm" color="gray.600">First 5 rows of processed data</Text>
                  </CardHeader>
                  <CardBody>
                    <Box overflowX="auto" maxH="300px" overflowY="auto">
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>Name</Th>
                            <Th>Address</Th>
                            <Th>Phone</Th>
                            <Th>Provider #</Th>
                            <Th>Status</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {previewData.slice(0, 5).map((lab, index) => (
                            <Tr key={index}>
                              <Td fontWeight="medium" fontSize="sm">{lab.name}</Td>
                              <Td fontSize="xs">{lab.address}</Td>
                              <Td fontSize="xs">{lab.contact_phone || 'N/A'}</Td>
                              <Td fontSize="xs">{lab.provider_number || 'N/A'}</Td>
                              <Td>
                                <Badge colorScheme="blue" variant="subtle" size="sm">
                                  Unclaimed
                                </Badge>
                              </Td>
                            </Tr>
                          ))}
                          {previewData.length > 5 && (
                            <Tr>
                              <Td colSpan={5} textAlign="center" color="gray.500" fontSize="xs">
                                ... and {previewData.length - 5} more rows
                              </Td>
                            </Tr>
                          )}
                        </Tbody>
                      </Table>
                    </Box>
                  </CardBody>
                </Card>
              )}

              {/* Upload Progress */}
              {uploading && (
                <Card>
                  <CardBody>
                    <VStack spacing={3}>
                      <Text fontWeight="medium">Uploading laboratories...</Text>
                      <Progress value={uploadProgress} size="lg" colorScheme="green" width="100%" />
                      <Text fontSize="sm" color="gray.600">
                        {Math.round(uploadProgress)}% complete
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>
              )}

              {/* Upload Results */}
              {uploadResults && (
                <Card>
                  <CardHeader>
                    <Heading size="sm">Upload Results</Heading>
                  </CardHeader>
                  <CardBody>
                    <SimpleGrid columns={3} spacing={4} mb={4}>
                      <Stat>
                        <StatLabel>Successfully Uploaded</StatLabel>
                        <StatNumber color="green.500">{uploadResults.success}</StatNumber>
                        <StatHelpText>
                          <Icon as={FaCheckCircle} color="green.500" mr={1} />
                          Laboratories added
                        </StatHelpText>
                      </Stat>
                      <Stat>
                        <StatLabel>Duplicates Skipped</StatLabel>
                        <StatNumber color="orange.500">{uploadResults.skipped || 0}</StatNumber>
                        <StatHelpText>
                          <Icon as={FaExclamationTriangle} color="orange.500" mr={1} />
                          Already exist
                        </StatHelpText>
                      </Stat>
                      <Stat>
                        <StatLabel>Failed</StatLabel>
                        <StatNumber color="red.500">{uploadResults.failed}</StatNumber>
                        <StatHelpText>
                          <Icon as={FaExclamationTriangle} color="red.500" mr={1} />
                          Errors encountered
                        </StatHelpText>
                      </Stat>
                    </SimpleGrid>

                    {uploadResults.duplicates && uploadResults.duplicates.length > 0 && (
                      <Box mb={4}>
                        <Text fontWeight="medium" mb={2} color="orange.600">Duplicates Skipped:</Text>
                        <Box maxH="150px" overflowY="auto">
                          {uploadResults.duplicates.map((duplicate, index) => (
                            <Alert status="warning" key={index} size="sm" mb={2}>
                              <AlertIcon boxSize={3} />
                              <AlertDescription fontSize="sm">
                                <strong>Row {duplicate.row}:</strong> {duplicate.name} - {duplicate.reason}
                              </AlertDescription>
                            </Alert>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {uploadResults.errors.length > 0 && (
                      <Box>
                        <Text fontWeight="medium" mb={2} color="red.600">Errors:</Text>
                        <Box maxH="150px" overflowY="auto">
                          {uploadResults.errors.map((error, index) => (
                            <Alert status="error" key={index} size="sm" mb={2}>
                              <AlertIcon boxSize={3} />
                              <AlertDescription fontSize="sm">
                                <strong>Row {error.row}:</strong> {error.name} - {error.error}
                              </AlertDescription>
                            </Alert>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </CardBody>
                </Card>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={() => { onUploadModalClose(); resetUploadModal(); }}>
                Cancel
              </Button>
              {previewData.length > 0 && !uploading && !uploadResults && duplicateCheckResults && (
                <Button
                  colorScheme="green"
                  leftIcon={<Icon as={FaUpload} />}
                  onClick={processUpload}
                  isLoading={uploading}
                  isDisabled={duplicateCheckResults.newCount === 0}
                >
                  Upload {duplicateCheckResults.newCount} New Labs
                  {duplicateCheckResults.duplicateCount > 0 && ` (${duplicateCheckResults.duplicateCount} duplicates skipped)`}
                </Button>
              )}
              {uploadResults && (
                <Button
                  colorScheme="blue"
                  onClick={() => { onUploadModalClose(); resetUploadModal(); }}
                >
                  Done
                </Button>
              )}
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default LabManagement; 